import React, { useState } from "react";
import { ExternalLink, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { useNeo4jVisualizer } from "@/context/neo4j-visualizer-context";

interface CitationRendererProps {
  citations: string[];
}

const CitationRenderer: React.FC<CitationRendererProps> = ({ citations }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Hook usage must be at the top level - not conditional
  const { openVisualizer } = useNeo4jVisualizer();

  if (!citations || citations.length === 0) {
    return (
      <div className="bg-surface-card border border-surface-border rounded-lg p-3 my-2">
        <div className="flex items-center gap-2 text-muted">
          <FileText className="h-3 w-3" />
          <span className="text-xs">No citations available</span>
        </div>
      </div>
    );
  }

  // Show first 3 citations inline, rest in expandable section
  const visibleCitations = isExpanded ? citations : citations.slice(0, 3);
  const hasMore = citations.length > 3;

  return (
    <div className="bg-surface-card border border-surface-border rounded-lg p-3 my-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="h-3 w-3 text-primary" />
          <span className="text-xs font-medium text-text-primary">
            Citations ({citations.length})
          </span>
        </div>
        {hasMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-xs text-muted hover:text-white transition-colors"
          >
            {isExpanded ? (
              <>
                <span>Show less</span>
                <ChevronUp className="h-3 w-3" />
              </>
            ) : (
              <>
                <span>Show all</span>
                <ChevronDown className="h-3 w-3" />
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Compact grid layout for citations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
        {visibleCitations.map((nodeId, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-1.5 bg-surface rounded hover:bg-surface-hover transition-colors group"
          >
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="text-xs text-muted font-mono bg-surface-dark px-1.5 py-0.5 rounded text-center min-w-[1.5rem]">
                {index + 1}
              </span>
              <span className="text-xs text-text-primary font-mono truncate" title={nodeId}>
                {nodeId}
              </span>
            </div>
            
            <button
              onClick={() => {
                openVisualizer(nodeId);
              }}
              className="text-primary hover:text-primary-dark transition-colors opacity-0 group-hover:opacity-100 ml-1 flex-shrink-0"
              title="View in graph"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      
      <div className="mt-2 pt-2 border-t border-surface-border">
        <p className="text-xs text-muted">
          Neo4j semantic model references
        </p>
      </div>
    </div>
  );
};

export default CitationRenderer;